import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { appAPI, shareAPI } from '../services/api';

const features = [
  {
    icon: '📤',
    title: 'Easy APK Upload',
    desc: 'Upload your Android apps in minutes. Simple admin panel with drag-and-drop support for APKs, icons, and screenshots.',
  },
  {
    icon: '🌍',
    title: 'Global CDN Delivery',
    desc: 'Fast downloads worldwide with our optimized content delivery network. Your users get the best download speeds anywhere.',
  },
  {
    icon: '📊',
    title: 'Download Analytics',
    desc: 'Track your app performance with detailed download statistics, user ratings, and engagement metrics in real-time.',
  },
  {
    icon: '⭐',
    title: 'Ratings & Reviews',
    desc: 'Built-in rating system lets users review your apps. Respond to feedback directly from the admin panel.',
  },
  {
    icon: '🔗',
    title: 'Smart Share Links',
    desc: 'Generate unique shareable links for each app. Track which links drive the most downloads and optimize your marketing.',
  },
  {
    icon: '🛡️',
    title: 'Secure & Reliable',
    desc: 'Enterprise-grade security with 99.9% uptime. Your apps are hosted on reliable infrastructure with automatic backups.',
  },
  {
    icon: '⚡',
    title: 'Instant Publishing',
    desc: 'Your app goes live immediately after upload. No approval wait times — start sharing with users right away.',
  },
  {
    icon: '📱',
    title: 'Multiple App Support',
    desc: 'Host unlimited Android applications under one account. Manage all your apps from a single dashboard.',
  },
  {
    icon: '💬',
    title: '24/7 Support',
    desc: 'Get help whenever you need it. Our support team is available around the clock via WhatsApp and phone.',
  },
];

const steps = [
  {
    num: '01',
    title: 'Contact Support',
    desc: 'Reach out to our team via WhatsApp or contact form to get started with hosting your Android app.',
  },
  {
    num: '02',
    title: 'Get Admin Access',
    desc: 'Receive your admin credentials and access our easy-to-use dashboard for managing your apps.',
  },
  {
    num: '03',
    title: 'Upload Your APK',
    desc: 'Upload your APK file, app icon, screenshots, and fill in app details through our simple upload interface.',
  },
  {
    num: '04',
    title: 'Share & Track',
    desc: 'Your app goes live instantly! Share the link with users and track downloads, ratings, and engagement.',
  },
];

const useCases = [
  {
    icon: '🛠️',
    title: 'Tools & Utilities',
    desc: 'Host productivity apps, system tools, file managers, and utility applications for Android users worldwide.',
  },
  {
    icon: '🎮',
    title: 'Games',
    desc: 'Share your Android games with players worldwide. From casual to hardcore gaming experiences, all welcome.',
  },
  {
    icon: '📱',
    title: 'Social & Communication',
    desc: 'Host messaging apps, social networks, and communication tools to connect people globally.',
  },
  {
    icon: '🎵',
    title: 'Entertainment',
    desc: 'Share media players, streaming apps, music apps, and entertainment content with your audience.',
  },
  {
    icon: '💼',
    title: 'Business & Productivity',
    desc: 'Host business apps, office tools, project management, and productivity applications for professionals.',
  },
  {
    icon: '📚',
    title: 'Education & Lifestyle',
    desc: 'Share educational apps, health trackers, lifestyle apps, and learning platforms with students and users.',
  },
];

const compatibleApps = [
  { icon: '🤖', name: 'Android 6.0+' },
  { icon: '📱', name: 'All Devices' },
  { icon: '🛠️', name: 'Tools' },
  { icon: '🎮', name: 'Games' },
  { icon: '🎵', name: 'Entertainment' },
  { icon: '💼', name: 'Business' },
  { icon: '📚', name: 'Education' },
  { icon: '🏥', name: 'Health' },
  { icon: '🍔', name: 'Food & Dining' },
  { icon: '✈️', name: 'Travel' },
  { icon: '📷', name: 'Photography' },
  { icon: '🌐', name: 'All Categories' },
];

const Home = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appTokens, setAppTokens] = useState({});

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await appAPI.getAllApps();
      const approvedApps = response.data.filter(app => app.status === 'approved');
      setApps(approvedApps);
      
      // Create share links for each app
      const tokens = {};
      for (const app of approvedApps) {
        try {
          const shareResponse = await shareAPI.createShareLink(
            app.id,
            `Download ${app.app_name}`,
            app.description,
            null
          );
          if (shareResponse.success) {
            tokens[app.id] = shareResponse.data.token;
          }
        } catch (error) {
          console.error(`Error creating share link for app ${app.id}:`, error);
        }
      }
      setAppTokens(tokens);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching apps:', error);
      setLoading(false);
    }
  };

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    
    // Get base URL without /api suffix
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'https://apibackend.sisateck.com';
    
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${cleanPath}`;
  };

  return (
    <div className="bg-[#0a0a0a] text-white">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>SisaTeck - Premium Android APK Hosting & Distribution Platform</title>
        <meta name="title" content="SisaTeck - Premium Android APK Hosting & Distribution Platform" />
        <meta name="description" content="Host and distribute your Android apps with SisaTeck. Fast CDN delivery, download analytics, ratings & reviews, smart share links, and secure APK hosting for developers worldwide." />
        <meta name="keywords" content="APK hosting, Android app distribution, APK download, app hosting platform, Android APK CDN, mobile app distribution, APK file hosting, Android developer tools, app analytics, APK marketplace, SisaTeck" />
        <link rel="canonical" href="https://sisateck.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sisateck.com/" />
        <meta property="og:title" content="SisaTeck - Premium Android APK Hosting & Distribution Platform" />
        <meta property="og:description" content="Host and distribute your Android apps with SisaTeck. Fast CDN delivery, analytics, ratings, and secure hosting for developers worldwide." />
        <meta property="og:image" content="https://sisateck.com/logo.png" />
        <meta property="og:site_name" content="SisaTeck" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://sisateck.com/" />
        <meta property="twitter:title" content="SisaTeck - Premium Android APK Hosting & Distribution" />
        <meta property="twitter:description" content="Host and distribute your Android apps with SisaTeck. Fast CDN delivery, analytics, and secure hosting for developers worldwide." />
        <meta property="twitter:image" content="https://sisateck.com/logo.png" />
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SisaTeck",
            "url": "https://sisateck.com",
            "logo": "https://sisateck.com/logo.png",
            "description": "Premium Android APK hosting and distribution platform for developers",
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+254793973389",
              "contactType": "Customer Service",
              "availableLanguage": "English"
            },
            "sameAs": []
          })}
        </script>
        
        {/* Structured Data - WebSite */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "SisaTeck",
            "url": "https://sisateck.com",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://sisateck.com/download?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>
      
      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
        {/* Background glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-green-500/3 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                APK Hosting Platform
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight mb-6">
                Host Your{' '}
                <span className="text-gradient">Android Apps</span>
                <br />
                With{' '}
                <span className="text-white">SisaTeck</span>
              </h1>

              <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-lg">
                Professional APK hosting platform for Android developers. Share your apps with users worldwide, track downloads, manage ratings, and grow your user base.
              </p>

              <div className="flex flex-wrap gap-4 mb-10">
                <a
                  href="/download"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-6 py-3.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-green-500/30 text-sm"
                >
                  📱 Browse APKs
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 bg-transparent hover:bg-white/5 text-white font-semibold px-6 py-3.5 rounded-xl border border-[#2a2a2a] hover:border-green-500/40 transition-all duration-200 text-sm"
                >
                  📤 Host Your App
                </a>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 pt-8 border-t border-[#2a2a2a]">
                {[
                  { num: `${apps.length}+`, label: 'Hosted Apps' },
                  { num: '99.9%', label: 'Uptime' },
                  { num: 'FREE', label: 'Hosting' },
                  { num: '24/7', label: 'Support' },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="text-2xl font-black text-green-500">{s.num}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Logo Visual */}
            <div className="flex justify-center items-center relative">
              <div className="relative w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 flex items-center justify-center">
                {/* Rings */}
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="absolute rounded-full border border-green-500/10 animate-ping"
                    style={{
                      width: `${100 + i * 20}%`,
                      height: `${100 + i * 20}%`,
                      animationDuration: `${2 + i}s`,
                      animationDelay: `${i * 0.5}s`,
                    }}
                  />
                ))}
                {/* Glow */}
                <div className="absolute inset-0 bg-green-500/10 rounded-full blur-2xl animate-pulse" />
                {/* Logo */}
                <img
                  src="/logo.png"
                  alt="Location Changer Pro"
                  className="relative z-10 w-56 h-56 sm:w-64 sm:h-64 lg:w-72 lg:h-72 object-contain drop-shadow-2xl"
                  style={{ filter: 'drop-shadow(0 20px 40px rgba(34,197,94,0.35))' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="bg-[#111111] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-green-500 text-xs font-bold uppercase tracking-widest">Simple Setup</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2 mb-4">
              Up & Running in <span className="text-gradient">4 Steps</span>
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
              No complicated configuration. No technical knowledge required. Just contact us and start hosting your Android apps today.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div
                key={step.num}
                className="relative bg-[#1a1a1a] border border-[#2a2a2a] hover:border-green-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-12 h-12 rounded-full bg-green-500/10 border-2 border-green-500 flex items-center justify-center text-green-500 font-black text-lg mb-5 group-hover:bg-green-500/20 transition-colors">
                  {step.num}
                </div>
                <h3 className="text-white font-bold text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 -right-3 w-6 h-0.5 bg-green-500/30 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section id="features" className="bg-[#0a0a0a] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-green-500 text-xs font-bold uppercase tracking-widest">What You Get</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2 mb-4">
              Packed with <span className="text-gradient">Hosting Features</span>
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
              Everything you need to host and manage your Android applications — from simple uploads to advanced analytics and tracking.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f) => (
              <div
                key={f.title}
                className="group bg-[#1a1a1a] border border-[#2a2a2a] hover:border-green-500/30 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/50 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-12 h-12 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-green-500/15 transition-colors">
                  {f.icon}
                </div>
                <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== USE CASES ===== */}
      <section id="use-cases" className="bg-[#111111] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-green-500 text-xs font-bold uppercase tracking-widest">Why People Use It</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2 mb-4">
              Endless <span className="text-gradient">Use Cases</span>
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
              From tools to entertainment, SisaTeck hosts all types of Android applications across different categories and industries.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((uc) => (
              <div
                key={uc.title}
                className="bg-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="text-3xl mb-4">{uc.icon}</div>
                <h3 className="text-white font-bold text-base mb-2">{uc.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== COMPATIBILITY ===== */}
      <section className="bg-[#0a0a0a] py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-green-500 text-xs font-bold uppercase tracking-widest">Platform Support</span>
            <h2 className="text-3xl sm:text-4xl font-black mt-2 mb-4">
              Host Apps For <span className="text-gradient">All Categories</span>
            </h2>
            <p className="text-gray-400 text-base max-w-xl mx-auto">
              SisaTeck supports all Android app categories. Whether your users are on phones or tablets, we've got you covered.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {compatibleApps.map((app) => (
              <div
                key={app.name}
                className="bg-[#1a1a1a] border border-[#2a2a2a] hover:border-green-500/40 rounded-xl p-4 text-center transition-all duration-200 hover:-translate-y-0.5 group"
              >
                <div className="text-2xl mb-2">{app.icon}</div>
                <div className="text-gray-400 group-hover:text-green-400 text-xs font-medium transition-colors">{app.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== AVAILABLE APKS ===== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]" id="apks">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black mb-4">
              Available <span className="text-gradient">APKs</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Browse and download our collection of powerful Android apps
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
              <p className="text-gray-500 mt-4">Loading apps...</p>
            </div>
          ) : apps.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No apps available yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {apps.map((app) => (
                <Link
                  key={app.id}
                  to={appTokens[app.id] ? `/s/${appTokens[app.id]}` : `/download`}
                  className="group bg-gradient-to-br from-[#111111] to-[#0a0a0a] border border-[#2a2a2a] hover:border-green-500/40 rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-green-500/10"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <img
                        src={getImageUrl(app.icon_url)}
                        alt={app.app_name}
                        className="w-16 h-16 rounded-xl object-cover border border-[#2a2a2a] group-hover:border-green-500/30 transition-colors"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg mb-1 group-hover:text-green-400 transition-colors truncate">
                        {app.app_name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">v{app.version}</p>
                      <p className="text-sm text-gray-400 line-clamp-2">
                        {app.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#2a2a2a] flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        ⭐ {app.rating || '4.5'}
                      </span>
                      <span className="flex items-center gap-1">
                        📦 {app.size_mb} MB
                      </span>
                    </div>
                    <span className="text-green-500 text-sm font-semibold group-hover:translate-x-1 transition-transform">
                      View →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {apps.length > 0 && (
            <div className="text-center mt-10">
              <Link
                to="/download"
                className="inline-flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 hover:border-green-500/40 text-green-400 font-semibold px-6 py-3 rounded-xl transition-all"
              >
                View All APKs →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-br from-[#111111] to-[#0d1f0d] border border-green-500/20 rounded-3xl p-10 sm:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 bg-green-500/3 rounded-3xl" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-px bg-gradient-to-r from-transparent via-green-500/60 to-transparent" />

            <div className="relative z-10">
              <div className="text-4xl mb-4">🚀</div>
              <h2 className="text-3xl sm:text-4xl font-black mb-4">
                Ready to <span className="text-gradient">Host Your App?</span>
              </h2>
              <p className="text-gray-400 text-base leading-relaxed mb-8 max-w-lg mx-auto">
                Join developers who trust SisaTeck for hosting their Android applications. Contact support to get started today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 text-black font-bold px-8 py-4 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-green-500/30"
                >
                  📤 Host Your App
                </a>
                <a
                  href="https://wa.me/254793973389?text=Hi,%20I%20want%20to%20host%20my%20Android%20app%20on%20SisaTeck"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/5 text-white font-semibold px-8 py-4 rounded-xl border border-[#2a2a2a] hover:border-green-500/40 transition-all duration-200"
                >
                  💬 Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
